import { LatLngLiteral } from "leaflet";
import {
    CreateEventPayload,
    AlertNotificationPayload,
} from "services/EventService.types";
import { CreateEventFormValues, InsightColorId } from "../types";
import { IOperationRuleAlertUser } from "stores/data/insightAlerts/insightAlertModel";
import { IStation } from "stores/data/oceanWeatherIntelligences/oceanWeatherIntelligenceModel";
import { MacroRegion } from "enum/MacroRegion";
import { RISK_TYPES, RiskTypeId } from "pages/Alert/components/AlertTypeSelector";

/**
 * Event Payload Mapper
 *
 * Utility functions to transform form values into the unified event payload
 * structure expected by the backend.
 *
 * Location priority (same as Alert creation):
 * 1. Custom point (station_id === 0) → use geofence with single point
 * 2. If stationId/regionId are provided (station_id > 0) → use station location
 * 3. If locationId is provided → use saved area location
 * 4. If geofence has points → use drawn/uploaded geofence
 */

// ═══════════════════════════════════════════════════════════════════════════
// HELPER FUNCTIONS
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Builds a single alert notification payload
 * @param values - Form values
 * @returns AlertNotificationPayload
 */
export function buildAlertNotification(
    values: CreateEventFormValues
): AlertNotificationPayload {
    const notification: AlertNotificationPayload = {
        id: 0, // New notification
        title: values.customTitle || values.name,
        description: values.customDescription || "",
        type: "before", // Default notification type
        minutesType: values.minAdvanceTime, // Required by backend
    };

    // Add timing configuration if provided
    if (values.minAdvanceTime !== undefined) {
        notification.minAdvanceTime = values.minAdvanceTime;
    }
    if (values.maxAdvanceTime !== undefined) {
        notification.maxAdvanceTime = values.maxAdvanceTime;
    }
    if (values.minimumEventDuration !== undefined) {
        notification.minimumEventDuration = values.minimumEventDuration;
    }

    // Add time window if provided
    if (values.startTime) {
        notification.startTime = values.startTime;
    }
    if (values.endTime) {
        notification.endTime = values.endTime;
    }

    // Add update preferences from receiptType
    if (values.receiptType && values.receiptType.length > 0) {
        notification.sendUpdates = values.receiptType.includes("sendUpdates");
        notification.sendCancellations = values.receiptType.includes("sendCancellations");
    }

    return notification;
}

/**
 * Extracts stationId and regionId from the stationRegion string
 * Format: "stationId-regionId"
 */
function parseStationRegion(stationRegion: string | undefined): {
    stationId: number | undefined;
    regionId: number | undefined;
} {
    if (!stationRegion) {
        return { stationId: undefined, regionId: undefined };
    }

    const parts = stationRegion.split("-");
    if (parts.length >= 2) {
        const stationId = parseInt(parts[0], 10);
        const regionId = parseInt(parts[1], 10);
        return {
            stationId: isNaN(stationId) ? undefined : stationId,
            regionId: isNaN(regionId) ? undefined : regionId,
        };
    }

    return { stationId: undefined, regionId: undefined };
}

/**
 * Determines the location fields to send based on the same priority as Alert creation:
 * 1. Custom point (station_id === 0) from existing location → operationRuleLocationId in insight
 * 2. locationId (saved area) - send locationId at root level
 * 3. stationId/regionId (station_id > 0) - clears locationId and geofence
 * 4. Custom point (station_id === 0) without existing location → geofence with single point
 * 5. geofence (drawn or uploaded area that wasn't saved)
 * 
 * Note: When user draws an area and saves it, isGeofenceSavedToLocation=true and locationId is set.
 * This should send locationId at root level, NOT operationRuleLocationId.
 * operationRuleLocationId is ONLY for custom points (station_id === 0) from existing locations.
 */
function resolveLocationFields(
    values: CreateEventFormValues,
    selectedGeofence: LatLngLiteral[] | undefined,
    selectedStation: IStation | null | undefined,
    _isGeofenceSavedToLocation?: boolean // Kept for backward compatibility but not used for loose area detection
): {
    locationId?: number;
    stationId?: number;
    regionId?: number;
    geofence?: LatLngLiteral[];
} {
    // Check if this is a custom point (station_id === 0) from an existing location
    // Custom point = user clicked on map where there's no real station, but it's linked to an existing location
    const isCustomPoint = selectedStation?.station_id === 0;
    const isCustomPointFromExistingLocation = isCustomPoint && values.locationId;

    // Priority 1: Custom point from existing location - operationRuleLocationId will be handled in insight object
    // Don't send any location fields at root level, it will be sent inside insight
    if (isCustomPointFromExistingLocation) {
        return {};
    }

    // Priority 2: Saved area location (locationId) - send locationId at root level
    // This includes:
    // - Areas drawn and then saved (isGeofenceSavedToLocation=true, locationId set)
    // - Areas selected from dropdown
    if (values.locationId) {
        return {
            locationId: values.locationId,
            // stationId, regionId, and geofence are undefined (not sent)
        };
    }

    // Priority 3: Station/Point selection (station_id > 0)
    if (selectedStation && selectedStation.station_id > 0) {
        return {
            stationId: selectedStation.station_id,
            regionId: selectedStation.region_id,
            // locationId and geofence are undefined (not sent)
        };
    }

    // Fallback to parsing stationRegion string if no selectedStation
    const { stationId, regionId } = parseStationRegion(values.stationRegion);
    if (stationId && stationId > 0) {
        return {
            stationId,
            regionId,
            // locationId and geofence are undefined (not sent)
        };
    }

    // Priority 4: Custom point (station_id === 0) without existing location
    // When user clicks on map where there's no station (and no existing locationId)
    if (isCustomPoint && selectedStation) {
        return {
            geofence: [{ lat: selectedStation.station_lat, lng: selectedStation.station_lon }],
            // stationId, regionId, and locationId are undefined (not sent)
        };
    }

    // Priority 5: Geofence (drawn or uploaded area that wasn't saved)
    const geofence = values.geofence?.length ? values.geofence : selectedGeofence;
    if (geofence && geofence.length > 0) {
        return {
            geofence,
            // stationId, regionId, and locationId are undefined (not sent)
        };
    }

    // No location specified
    return {};
}

// ═══════════════════════════════════════════════════════════════════════════
// MAIN MAPPER FUNCTION
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Maps form values to the unified event payload structure
 *
 * This is the main function that transforms CreateEventFormValues
 * into CreateEventPayload for the unified /v1/event endpoint.
 *
 * @param values - Form values from the Event creation form
 * @param selectedUsers - Users selected to receive alerts
 * @param macroRegionOrigin - Macro region from store (optional)
 * @param selectedGeofence - Geofence from store (optional, fallback if not in values)
 * @param selectedStation - Selected station from store (for custom point detection)
 * @param isGeofenceSavedToLocation - If true, the geofence is from an existing saved location (loose area)
 * @returns CreateEventPayload ready to send to the API
 */
export function mapFormValuesToEventPayload(
    values: CreateEventFormValues,
    selectedUsers: IOperationRuleAlertUser[],
    macroRegionOrigin?: MacroRegion,
    selectedGeofence?: LatLngLiteral[],
    selectedStation?: IStation | null,
    isGeofenceSavedToLocation?: boolean
): CreateEventPayload {
    // Resolve location fields using the same priority as Alert creation
    const locationFields = resolveLocationFields(values, selectedGeofence, selectedStation, isGeofenceSavedToLocation);

    // Build the base payload with control flags and common data
    const payload: CreateEventPayload = {
        // Control flags based on toggle blocks
        shouldCreateInsight: values.block1Active,
        shouldCreateAlert: values.block2Active,

        // Common data
        name: values.name,
        conditionGroups: values.conditionGroups || [],

        // Location fields (only the relevant ones will be set)
        ...locationFields,
    };

    // Add macroRegionOrigin if provided from store or form
    if (macroRegionOrigin) {
        payload.macroRegionOrigin = macroRegionOrigin;
    }

    // Build insight section if Block 1 is active
    if (values.block1Active && values.dashboardId) {
        // Determine the operationRuleLocationId to send inside insight:
        // 1. Explicit operationRuleLocationId from form (set from prefilledLocation.id for all location types)
        // 2. Fallback: locationId for custom points from existing locations (legacy behavior)
        const isCustomPoint = selectedStation?.station_id === 0;
        const isCustomPointFromExistingLocation = isCustomPoint && values.locationId;
        const insightLocationId = values.operationRuleLocationId
            || (isCustomPointFromExistingLocation ? values.locationId : undefined);

        payload.insight = {
            dashboardId: values.dashboardId,
            restrictionType: values.restrictionType,
            priorityId: values.insightColor as InsightColorId,
            resume: values.resume ?? "",
            // Include operationRuleLocationId so the backend associates the event with the correct location
            ...(insightLocationId && { operationRuleLocationId: insightLocationId }),
        };
    }

    // Add alertType at root level (required by backend)
    // When restrictionType is false (Permissive), always use "no_category"
    // Otherwise, must use backendKey from RISK_TYPES, not the templateType ID
    if (values.restrictionType === false) {
        payload.alertType = "no_category";
    } else {
        const templateType = values.alertType || values.templateType;
        if (templateType) {
            const riskType = RISK_TYPES[templateType as RiskTypeId];
            // Use backendKey if available, otherwise fall back to the raw value
            payload.alertType = riskType?.backendKey || templateType;
        }
    }

    // Build alert section if Block 2 is active
    if (values.block2Active) {
        // Pass full user objects as recipients (backend expects complete IOperationRuleAlertUser)
        // Handle both flat structure and nested structure (where user itself is wrapped in userId)
        const recipients = selectedUsers.map(user => {
            // If user.userId is an object, it means the user object is nested incorrectly
            if (user.userId && typeof user.userId === 'object') {
                // Return the nested object directly (it's the actual user)
                return user.userId as IOperationRuleAlertUser;
            }
            // Normal flat structure - return user as-is
            return {
                id: user.id ?? 0,
                userId: user.userId,
                userName: user.userName,
                isWhatsappAllowed: user.isWhatsappAllowed ?? false,
                isPushAllowed: user.isPushAllowed ?? false,
                isEmailAllowed: user.isEmailAllowed ?? false,
            };
        });

        payload.alert = {
            recipients,
            notifications: [buildAlertNotification(values)],
            channels: values.channels || [],
        };
    }

    // Include sensor alert configuration only when alert is active and at least
    // one slice (rain sensors OR lightning point) is configured. Spec 043 allows
    // lightning-only events; rain-only continues to work as before.
    if (
        values.block2Active &&
        (values.sensorAlertConfig?.sensors?.length ||
            values.sensorAlertConfig?.lightning)
    ) {
        payload.sensorAlertConfig = values.sensorAlertConfig;
    }

    return payload;
}

// ═══════════════════════════════════════════════════════════════════════════
// VALIDATION
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Validation error keys for i18n
 */
export const VALIDATION_ERROR_KEYS = {
    NAME_REQUIRED: "validation.nameRequired",
    AT_LEAST_ONE_OPTION: "validation.atLeastOneOption",
    LOCATION_REQUIRED: "validation.locationRequired",
    INSIGHT_DATA_REQUIRED: "validation.insightDataRequired",
    DASHBOARD_REQUIRED: "validation.dashboardRequired",
    RECIPIENTS_REQUIRED: "validation.recipientsRequired",
    CHANNELS_REQUIRED: "validation.channelsRequired",
} as const;

/**
 * Validates that the payload has all required data based on control flags
 *
 * @param payload - The payload to validate
 * @returns Object with isValid flag and error keys for translation
 */
export function validateEventPayload(payload: CreateEventPayload): {
    isValid: boolean;
    errors: string[];
} {
    const errors: string[] = [];

    // Basic validation
    if (!payload.name || payload.name.trim() === "") {
        errors.push(VALIDATION_ERROR_KEYS.NAME_REQUIRED);
    }

    // At least one block must be active
    if (!payload.shouldCreateInsight && !payload.shouldCreateAlert) {
        errors.push(VALIDATION_ERROR_KEYS.AT_LEAST_ONE_OPTION);
    }

    // Location validation - at least one location type must be provided
    const hasLocation =
        payload.locationId ||
        payload.insight?.operationRuleLocationId ||
        (payload.stationId && payload.stationId > 0) ||
        (payload.geofence && payload.geofence.length > 0);

    if (!hasLocation) {
        errors.push(VALIDATION_ERROR_KEYS.LOCATION_REQUIRED);
    }

    // Insight validation
    if (payload.shouldCreateInsight) {
        if (!payload.insight) {
            errors.push(VALIDATION_ERROR_KEYS.INSIGHT_DATA_REQUIRED);
        } else if (!payload.insight.dashboardId) {
            errors.push(VALIDATION_ERROR_KEYS.DASHBOARD_REQUIRED);
        }
    }

    // Alert validation
    if (payload.shouldCreateAlert) {
        if (!payload.alert) {
            errors.push(VALIDATION_ERROR_KEYS.INSIGHT_DATA_REQUIRED);
        } else {
            if (!payload.alert.recipients || payload.alert.recipients.length === 0) {
                errors.push(VALIDATION_ERROR_KEYS.RECIPIENTS_REQUIRED);
            }
            if (!payload.alert.channels || payload.alert.channels.length === 0) {
                errors.push(VALIDATION_ERROR_KEYS.CHANNELS_REQUIRED);
            }
        }
    }

    return {
        isValid: errors.length === 0,
        errors,
    };
}
